import { BusinessObjectBase, decodeDecimals } from "./base";
import { userSchema } from "@schemas/business-objects/user";
import type { RoleAssignment } from "./role-assignment";
import type { Id } from "../base-types";

export interface UserData {
  readonly id: Id;
  readonly email: string;
  readonly lastSeen?: Date;
  readonly roleAssignments?: RoleAssignment[];
}

export interface User extends Readonly<UserData> {}
type UserCreateWithChildrenData = Partial<Omit<UserData, "roleAssignments">> & {
  roleAssignments?: Partial<RoleAssignment>[];
};

export class User extends BusinessObjectBase {
  static readonly entityName = "User" as const;
  static readonly plural = "users";
  static schema = userSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<User[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params["filter"] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new User(data));
  }

  static async findOne(filter?: any): Promise<User | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params["filter"] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new User(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<User> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params["filter"] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new User(data);
  }

  static async create(data: Omit<UserData, "id">): Promise<User> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new User(result);
  }

  static async createMany(data: Omit<UserData, "id">[]): Promise<User[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new User(item));
  }

  static async createWithChildren(
    data: UserCreateWithChildrenData,
  ): Promise<User> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new User(result);
  }

  static async createManyWithChildren(
    data: UserCreateWithChildrenData[],
  ): Promise<User[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new User(item));
  }

  static async updateById(id: Id, updates: Partial<UserData>): Promise<User> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new User(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async currentUser(filter?: any): Promise<User | null> {
    const url = `${this.baseUrl}/${this.plural}/current-user`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined) queryParams.set("filter", JSON.stringify(filter));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<User | null>(fullUrl).then((value) =>
      decodeDecimals(userSchema, value),
    );
  }
}
